import {CdkMenuTrigger} from "@angular/cdk/menu"
import {ChangeDetectionStrategy, Component, computed, input, output} from "@angular/core"
import {
    AssetActionsMenuComponent,
    AssetCellChipComponent,
    AssetDimensionOverlayComponent,
    AssetDownloadMenuComponent,
    AssetGridCellComponent,
    AssetMenuAction,
} from "@common/components/asset-grid"
import {LoadingSpinnerIconComponent} from "@common/components/progress/loading-spinner-icon/loading-spinner-icon.component"
import {JobState} from "@generated"
import type {MaterialAssetGridOutputFragment} from "@platform/components/materials/material-asset-grid/material-asset-grid/material-asset-grid.generated"

type TileableResult = NonNullable<MaterialAssetGridOutputFragment["result"]>

type TileableState = {kind: "empty"} | {kind: "loading"} | {kind: "failed"} | {kind: "complete"; result: TileableResult; isLegacyZip: boolean}

export type TileableDimensionsCm = {width: number; height: number}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-tileable-asset-cell",
    templateUrl: "./tileable-asset-cell.component.html",
    styleUrls: ["./tileable-asset-cell.component.scss"],
    imports: [
        AssetActionsMenuComponent,
        AssetCellChipComponent,
        AssetDimensionOverlayComponent,
        AssetDownloadMenuComponent,
        AssetGridCellComponent,
        CdkMenuTrigger,
        LoadingSpinnerIconComponent,
    ],
})
export class TileableAssetCellComponent {
    readonly $output = input<MaterialAssetGridOutputFragment | null | undefined>(null, {alias: "output"})
    readonly $pending = input<boolean>(false, {alias: "pending"})
    readonly $dimensionsCm = input<TileableDimensionsCm | null>(null, {alias: "dimensionsCm"})
    readonly $hasCyclesMaterial = input<boolean>(false, {alias: "hasCyclesMaterial"})
    readonly $canEdit = input<boolean>(false, {alias: "canEdit"})
    readonly $canDownload = input<boolean>(false, {alias: "canDownload"})

    readonly render = output<void>()
    readonly rerender = output<void>()
    readonly remove = output<void>()
    readonly cancel = output<void>()
    readonly retry = output<void>()
    readonly preview = output<void>()

    readonly $state = computed<TileableState>(() => {
        const output = this.$output()
        const jobState = output?.job?.state
        if (this.$pending() || (jobState && isJobRunning(jobState))) return {kind: "loading"}
        if (!output) return {kind: "empty"}
        if (jobState === JobState.Failed || jobState === JobState.Cancelled) return {kind: "failed"}
        if (output.result) {
            return {kind: "complete", result: output.result, isLegacyZip: output.result.mediaType === "application/zip"}
        }
        return {kind: "empty"}
    })

    readonly $widthLabel = computed(() => formatCmDimensionLabel(this.$dimensionsCm()?.width))
    readonly $heightLabel = computed(() => formatCmDimensionLabel(this.$dimensionsCm()?.height))

    readonly actions: AssetMenuAction[] = [
        {id: "rerender", label: "Regenerate", icon: "fa-rotate-reverse"},
        {id: "remove", label: "Delete", icon: "fa-trash-can", variant: "destructive"},
    ]

    onAction(actionId: string) {
        if (actionId === "rerender") this.rerender.emit()
        else if (actionId === "remove") this.remove.emit()
    }
}

function isJobRunning(state: JobState): boolean {
    return state === JobState.Init || state === JobState.Runnable || state === JobState.Running
}

const CM_PER_INCH = 2.54

function formatCmDimensionLabel(sizeCm: number | undefined): string {
    if (!sizeCm || !isFinite(sizeCm)) return ""
    const inches = sizeCm / CM_PER_INCH
    return `${Math.round(sizeCm)}cm (${inches.toFixed(1)}in)`
}
